import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Settings2, X } from "lucide-react";
import { useDeviceForTenant, useDeviceConfigurationForTenant, usePatchDeviceConfiguration } from "../../api/devices";
import { useDeviceModels, useDeviceTypes } from "../../api/deviceTypes";
import { deviceModelLabel } from "../../api/deviceModelLabel";
import { extractErrorMessage } from "../../api/errors";
import { getRole, hasMinRole, stopActingAsCompany } from "../../auth/session";
import type { DeviceConfiguration, DeviceTypeFieldDefinition } from "../../api/types";
import { getAdminShadowSettingsRedirectPath } from "./shadowSettingsRouting";
import { GatewayShadowSettingsPage } from "./GatewayShadowSettingsPage";
import { usesGatewayConfigPage } from "./gatewayConfig";
import { describePhoneForError, looksLikeMultipleNumbers, sanitisePhoneNumber } from "../../lib/phone";
import { ConfigFieldsForm } from "../../components/config/ConfigFieldsForm";
import {
  buildConfigOverrides,
  clearPaths,
  initialConfigValues,
  invalidSecretFields,
  isButtonSettingPath,
  isValueOnlyPath,
  listenInNumbersPath,
  pruneToAllowedPaths,
  type ConfigValues,
} from "../../components/config/configForm";

type ShadowContactKey = "sos" | "leftButton" | "middleButton" | "rightButton";
type ShadowContacts = Record<ShadowContactKey, string>;

const emptyContacts: ShadowContacts = {
  sos: "",
  leftButton: "",
  middleButton: "",
  rightButton: "",
};

const allowlistPath = "inbound.auto_answer_allowlist";

// The SOS group decides whether a device places a call at all and who it
// rings, and the button settings decide what each button does, so both are
// saved under stricter rules than the rest of this page:
//
//  - A setting is only ever sent as a real value. Removing a key from the
//    override reaches the shadow as null, which deletes it from the desired
//    state without the device being told: it keeps what it had while this page
//    shows something else. So "Reset to defaults" writes the defaults, an
//    emptied response list is written as [] and an emptied SMS message as "".
//  - A setting is only sent when this device already has it stored or the
//    operator changed it here. A configuration deployed to a group reaches the
//    shadow without leaving a per-device override, so this page cannot see it;
//    writing the form's defaults on an unrelated save would silently undo it.
//  - With nothing stored, the controls open on what the device reports.
// The paths themselves are named by isValueOnlyPath, which the page that
// creates a device shares.
const phoneNumberSuffix = ".phoneNumber";

// The numbers that respond to an SOS. Edited as rows like the allowlist, not as
// a generic schema field: the generic form REMOVES a blanked list from the
// override, and the device would go on answering and dialling numbers an
// operator had cleared.
const responseNumbersPath = "sos.response_numbers";
// The firmware holds three; the schema's maxItems is used when it carries one.
const defaultMaxResponseNumbers = 3;

// The silent listen-in number (listenInNumbersPath). The device holds one, so it
// is edited as a single box, but it travels as a list: an emptied box is written
// as [] so the device forgets the number. A removed key would leave the device
// answering that caller with the speaker off. It is saved under the same rule as
// the SOS group (isValueOnlyPath). It may also be an SOS response number, an
// allowlist number or a button number: the device answers it as a listen-in
// call whatever else it is, so the page does not refuse the overlap.

// Check-in cadence. A DEVICE SETTING, not part of any device-type schema: it
// configures the FOTA client itself, so it applies to every device type and is
// deliberately absent from the generated config form. The backend accepts and
// range-checks it (deviceSettingFields in the devicetype module); the control
// for it is purpose-built below.
const checkInPath = "checkInMinutes";
const checkInDefaultMinutes = 60;
const checkInMinMinutes = 1;
const checkInMaxMinutes = 1440;
// 15 digits is the ITU-T E.164 maximum, and matches strictPhonePattern in
// backend/modules/devicetype/validation.go. The ceiling is load-bearing:
// without it two numbers pasted into one row merge into a valid-looking
// 20-digit value. See looksLikeMultipleNumbers in lib/phone.ts.
const allowlistPhonePattern = /^\+?[0-9]{1,15}$/;
const maxAllowlistNumbers = 100;

const contactFields: Array<{ key: ShadowContactKey; label: string; path: string; placeholder: string; aliases: string[] }> = [
  { key: "sos", label: "SOS", path: "buttons.sos.phoneNumber", placeholder: "+27 82 000 0000", aliases: ["sos", "SOS"] },
  { key: "leftButton", label: "Left button", path: "buttons.button1.phoneNumber", placeholder: "+27 82 000 0001", aliases: ["leftButton", "left_button", "left button"] },
  { key: "middleButton", label: "Middle button", path: "buttons.button2.phoneNumber", placeholder: "+27 82 000 0002", aliases: ["middleButton", "middle_button", "middle button"] },
  { key: "rightButton", label: "Right button", path: "buttons.button3.phoneNumber", placeholder: "+27 82 000 0003", aliases: ["rightButton", "right_button", "right button"] },
];

// The three programmable buttons: the contact number each already had, plus
// what the button does and the SMS text it sends. The SOS button is not one of
// them. The action and message paths sit beside the number, under the same
// buttons.buttonN object the device reads.
const programmableButtons = contactFields
  .filter((field) => field.key !== "sos")
  .map((field) => {
    const base = field.path.slice(0, -phoneNumberSuffix.length);
    return { ...field, actionPath: `${base}.action`, messagePath: `${base}.message` };
  });
type ProgrammableButton = (typeof programmableButtons)[number];

// The actions that use the button's number. The others send to the platform.
const actionsNeedingNumber = ["call", "sms"];
const smsAction = "sms";
// One unsegmented SMS; the schema's max is used when the field carries one.
const defaultMaxMessageLength = 160;
// What the device can send in a message: space to tilde, except the backtick,
// which the GSM alphabet an SMS is sent in does not have. Anything else (an
// accent, a curly quote pasted from a document, an emoji) cannot be sent.
const smsTextPattern = /^[\x20-\x5f\x61-\x7e]*$/;
// These are sent as two codes each, so each takes two of the message's places.
// The backend counts the same way (the sms_gsm7 format).
const smsTwoPlaceChars = /[\^{}[\]~|\\]/g;

// How many of its places a message takes: one per character, two for each of
// the characters above.
function smsLength(message: string): number {
  return message.length + (message.match(smsTwoPlaceChars)?.length ?? 0);
}

function readUnknownAtPath(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  let current: unknown = source;
  for (const part of path.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function readValueAtPath(source: Record<string, unknown> | undefined, path: string): string {
  const value = readUnknownAtPath(source, path);
  return typeof value === "string" ? value : "";
}

function readStringArrayAtPath(source: Record<string, unknown> | undefined, path: string): string[] {
  const value = readUnknownAtPath(source, path);
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function cloneOverrides(source: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!source) return {};
  return JSON.parse(JSON.stringify(source)) as Record<string, unknown>;
}

function setNestedValue(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let current = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    const existing = current[part];
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

function deleteNestedValue(target: Record<string, unknown>, path: string): void {
  const parts = path.split(".");
  const parents: Array<{ node: Record<string, unknown>; key: string }> = [];
  let current: unknown = target;
  for (const part of parts.slice(0, -1)) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return;
    parents.push({ node: current as Record<string, unknown>, key: part });
    current = (current as Record<string, unknown>)[part];
  }
  if (!current || typeof current !== "object" || Array.isArray(current)) return;
  delete (current as Record<string, unknown>)[parts[parts.length - 1]];

  for (let index = parents.length - 1; index >= 0; index -= 1) {
    const { node, key } = parents[index];
    const child = node[key];
    if (child && typeof child === "object" && !Array.isArray(child) && Object.keys(child).length === 0) {
      delete node[key];
    }
  }
}

function readContactValue(config: DeviceConfiguration | undefined, field: (typeof contactFields)[number]): string {
  const sources = [config?.overrides, config?.payload].filter(Boolean) as Record<string, unknown>[];
  for (const source of sources) {
    const pathValue = readValueAtPath(source, field.path);
    if (pathValue) return pathValue;

    const contactNumbers = source.contactNumbers;
    if (contactNumbers && typeof contactNumbers === "object" && !Array.isArray(contactNumbers)) {
      for (const alias of field.aliases) {
        const value = (contactNumbers as Record<string, unknown>)[alias];
        if (typeof value === "string") return value;
      }
    }
    for (const alias of field.aliases) {
      const value = source[alias];
      if (typeof value === "string") return value;
    }
  }
  return "";
}

function buildContacts(config: DeviceConfiguration | undefined): ShadowContacts {
  return contactFields.reduce<ShadowContacts>((acc, field) => {
    acc[field.key] = readContactValue(config, field);
    return acc;
  }, { ...emptyContacts });
}

function readPhoneList(config: DeviceConfiguration | undefined, path: string): string[] {
  const sources = [config?.overrides, config?.payload].filter(Boolean) as Record<string, unknown>[];
  for (const source of sources) {
    const values = readStringArrayAtPath(source, path);
    if (values.length > 0) return values;
  }
  return [""];
}

function hasValueAtPath(source: Record<string, unknown> | undefined, path: string): boolean {
  const value = readUnknownAtPath(source, path);
  return value !== undefined && value !== null;
}

// The response numbers to open on: the list stored for this device if there is
// one - an empty stored list is a decision and wins - otherwise what the
// device reports it holds.
function readResponseNumbers(config: DeviceConfiguration | undefined): string[] {
  for (const source of [config?.overrides, config?.reported?.payload]) {
    if (!Array.isArray(readUnknownAtPath(source, responseNumbersPath))) continue;
    const values = readStringArrayAtPath(source, responseNumbersPath);
    return values.length > 0 ? values : [""];
  }
  return [""];
}

// The list the device reports, or null when it has not reported one.
function readReportedResponseNumbers(config: DeviceConfiguration | undefined): string[] | null {
  const source = config?.reported?.payload;
  if (!Array.isArray(readUnknownAtPath(source, responseNumbersPath))) return null;
  return readStringArrayAtPath(source, responseNumbersPath);
}

// The listen-in number to open on: the one stored for this device if a list is
// stored - an empty stored list is a decision and wins - otherwise the one the
// device reports.
function readListenInNumber(config: DeviceConfiguration | undefined): string {
  for (const source of [config?.overrides, config?.reported?.payload]) {
    if (!Array.isArray(readUnknownAtPath(source, listenInNumbersPath))) continue;
    return readStringArrayAtPath(source, listenInNumbersPath)[0] ?? "";
  }
  return "";
}

// The listen-in list the device reports, or null when it has not reported one.
function readReportedListenInNumbers(config: DeviceConfiguration | undefined): string[] | null {
  const source = config?.reported?.payload;
  if (!Array.isArray(readUnknownAtPath(source, listenInNumbersPath))) return null;
  return readStringArrayAtPath(source, listenInNumbersPath);
}

// The SMS text to open on: what is stored for this device if anything is - a
// stored empty message is a decision and wins - otherwise what the device
// reports, then the configuration deployed to it.
function readButtonMessages(config: DeviceConfiguration | undefined): Record<string, string> {
  const messages: Record<string, string> = {};
  for (const button of programmableButtons) {
    messages[button.messagePath] = "";
    for (const source of [config?.overrides, config?.reported?.payload, config?.payload]) {
      const value = readUnknownAtPath(source, button.messagePath);
      if (typeof value !== "string") continue;
      messages[button.messagePath] = value;
      break;
    }
  }
  return messages;
}

// A message is one line: a line break is not a character the device accepts.
function singleLine(value: string): string {
  return value.replace(/[\r\n]+/g, " ");
}

function validateButtonMessage(label: string, message: string, max: number): string {
  if (!smsTextPattern.test(message)) {
    const bad = Array.from(message).find((char) => !smsTextPattern.test(char));
    return `${label} SMS message contains "${bad}", which the device cannot send. Use plain letters, digits and punctuation - no accents, curly quotes, backticks or emoji.`;
  }
  const length = smsLength(message);
  if (length > max) {
    return length === message.length
      ? `${label} SMS message is ${length} characters. The device holds up to ${max}.`
      : `${label} SMS message counts as ${length} characters, because each of ^ { } [ ] ~ | \\ counts as two. The device holds up to ${max}.`;
  }
  return "";
}

function readAllowlist(config: DeviceConfiguration | undefined): string[] {
  return readPhoneList(config, allowlistPath);
}

function normaliseAllowlist(numbers: string[]): string[] {
  return numbers.map((number) => sanitisePhoneNumber(number)).filter(Boolean);
}

function validateAllowlist(numbers: string[]): string {
  const multiple = numbers.find((number) => looksLikeMultipleNumbers(number));
  if (multiple !== undefined) {
    return "Each row holds one number. Paste the second number into its own row using Add number.";
  }
  const values = normaliseAllowlist(numbers);
  if (values.length > maxAllowlistNumbers) {
    return `Auto-answer allowlist can contain up to ${maxAllowlistNumbers} numbers.`;
  }
  const invalid = values.find((number) => !allowlistPhonePattern.test(number));
  if (invalid) {
    return `Invalid allowlist number "${describePhoneForError(invalid)}". Use an optional + followed by 1-15 digits. Spaces, dashes and hidden characters from a contacts app are removed for you.`;
  }
  return "";
}

// Same rules as the allowlist, with the device's own capacity as the ceiling:
// the backend refuses a longer list, and saying so here names the control the
// operator is looking at instead of a schema path.
function validateResponseNumbers(numbers: string[], max: number): string {
  const multiple = numbers.find((number) => looksLikeMultipleNumbers(number));
  if (multiple !== undefined) {
    return "Each row holds one number. Put the second response number in its own row using Add response number.";
  }
  const values = normaliseAllowlist(numbers);
  if (values.length > max) {
    return `The device holds up to ${max} SOS response numbers.`;
  }
  const invalid = values.find((number) => !allowlistPhonePattern.test(number));
  if (invalid) {
    return `Invalid SOS response number "${describePhoneForError(invalid)}". Use an optional + followed by 1-15 digits. Spaces, dashes and hidden characters from a contacts app are removed for you.`;
  }
  // The device drops an exact repeat and flags the whole list as refused, so
  // what it reports would never match what was saved.
  const repeated = values.find((number, index) => values.indexOf(number) !== index);
  if (repeated) {
    return `SOS response number "${describePhoneForError(repeated)}" is listed twice. Enter each number once.`;
  }
  return "";
}

// One number or none. There is deliberately no check against the SOS response
// numbers, the allowlist or the button numbers: the overlap is allowed.
function validateListenInNumber(raw: string): string {
  if (!raw.trim()) return ""; // blank clears the number
  if (looksLikeMultipleNumbers(raw)) {
    return "The device holds one silent listen-in number. Remove the extra number.";
  }
  const cleaned = sanitisePhoneNumber(raw);
  if (!allowlistPhonePattern.test(cleaned)) {
    return `Invalid silent listen-in number "${describePhoneForError(cleaned)}". Use an optional + followed by 1-15 digits. Spaces, dashes and hidden characters from a contacts app are removed for you.`;
  }
  return "";
}

// The contact fields previously had no client-side check at all, so an
// unfixable number was submitted and came back as a server-side schema error
// naming the raw path. Validating here uses the same rules as the allowlist
// and names the button the user is looking at.
function validateContacts(contacts: ShadowContacts): string {
  for (const field of contactFields) {
    const raw = contacts[field.key];
    if (!raw.trim()) continue; // blank clears the contact
    if (looksLikeMultipleNumbers(raw)) {
      return `${field.label} holds one number. Remove the extra number.`;
    }
    const cleaned = sanitisePhoneNumber(raw);
    if (!allowlistPhonePattern.test(cleaned)) {
      return `Invalid ${field.label} number "${describePhoneForError(cleaned)}". Use an optional + followed by 1-15 digits. Spaces, dashes and hidden characters from a contacts app are removed for you.`;
    }
  }
  return "";
}

// A blank secret is not sent (the stored one is kept), so only a typed one is
// checked - the same rule the backend applies.
function validateSecrets(fields: DeviceTypeFieldDefinition[], values: ConfigValues): string {
  const bad = invalidSecretFields(fields, values);
  if (bad.length === 0) return "";
  return `${bad.map((f) => f.label).join(", ")}: 8–64 characters, letters, digits, punctuation and spaces only, no space at the start or end.`;
}

function readSafeReturnPath(value: string | null, fallback: string): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}

// responseNumbers is null when the list is not to be written: the device
// type's schema has no response-number field, or the device has none stored
// and the operator did not touch it. The key is then left exactly as stored.
// messages holds only the SMS texts that are to be written, by path, under the
// same rule. listenInNumber is null under the same rule as responseNumbers.
function buildOverrides(
  config: DeviceConfiguration | undefined,
  contacts: ShadowContacts,
  allowlistNumbers: string[],
  checkIn: string,
  responseNumbers: string[] | null,
  messages: Record<string, string>,
  listenInNumber: string | null,
): Record<string, unknown> {
  const overrides = cloneOverrides(config?.overrides);
  for (const field of contactFields) {
    const value = sanitisePhoneNumber(contacts[field.key]);
    if (value) {
      setNestedValue(overrides, field.path, value);
    } else {
      deleteNestedValue(overrides, field.path);
    }
  }
  setNestedValue(overrides, allowlistPath, normaliseAllowlist(allowlistNumbers));
  if (responseNumbers !== null) {
    // An empty list is written as [] rather than removed.
    setNestedValue(overrides, responseNumbersPath, normaliseAllowlist(responseNumbers));
  }
  if (listenInNumber !== null) {
    // An emptied number is written as [] rather than removed.
    setNestedValue(overrides, listenInNumbersPath, normaliseAllowlist([listenInNumber]));
  }
  for (const [path, message] of Object.entries(messages)) {
    // An emptied message is written as "" rather than removed.
    setNestedValue(overrides, path, message);
  }
  const minutes = clampCheckInMinutes(checkIn);
  if (minutes === null) {
    // Blank means "leave the device on whatever it has" — including the
    // firmware's own 60-minute default on a device never set. Removing the key
    // is not the same as writing a value, and writing a default here would
    // silently pin every device the first time an unrelated setting is saved.
    deleteNestedValue(overrides, checkInPath);
  } else {
    setNestedValue(overrides, checkInPath, minutes);
  }
  return overrides;
}

// Reads the cadence currently in force: an explicit per-device override wins,
// then whatever the device last reported, then the firmware default.
function readCheckInMinutes(config: DeviceConfiguration | undefined): string {
  const fromOverride = (config?.overrides as Record<string, unknown> | undefined)?.[checkInPath];
  if (typeof fromOverride === "number") return String(fromOverride);
  const fromPayload = (config?.payload as Record<string, unknown> | undefined)?.[checkInPath];
  if (typeof fromPayload === "number") return String(fromPayload);
  return "";
}

// Anything under 15 minutes is a testing cadence, not an operating one. Worth
// saying out loud on the form rather than discovering it on a data bill.
function isAggressiveCadence(raw: string): boolean {
  const minutes = clampCheckInMinutes(raw);
  return minutes !== null && minutes < 15;
}

// Returns null for "not set" (blank), otherwise the value clamped into range.
// The backend range-checks this too; clamping here is so the operator sees what
// will be sent rather than getting a save rejected after the fact.
function clampCheckInMinutes(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < checkInMinMinutes) return checkInMinMinutes;
  if (rounded > checkInMaxMinutes) return checkInMaxMinutes;
  return rounded;
}

// The shadow-settings route serves every device type. A Teltonika gateway
// reports its whole running config and adopts changes made on the unit itself,
// so it gets its own page; everything else keeps the two-way comms page. The
// configuration query is shared (same key), so choosing costs no extra request.
export function DeviceShadowSettingsPage() {
  const { imei = "" } = useParams<{ imei: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdminScope = location.pathname.startsWith("/admin/");
  const scopedTenantId = searchParams.get("tenantId")?.trim() || undefined;
  const configQuery = useDeviceConfigurationForTenant(imei, scopedTenantId, {
    requireTenant: isAdminScope,
    useActingTenantFallback: !isAdminScope,
  });
  // Until the configuration arrives the device type is unknown; rendering
  // either page would flash the wrong one.
  if (configQuery.isLoading) {
    return <div className="py-12 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading shadow settings...</div>;
  }
  if (usesGatewayConfigPage(configQuery.data?.data?.deviceTypeId)) {
    return <GatewayShadowSettingsPage />;
  }
  return <TwoWayCommsShadowSettingsPage />;
}

function TwoWayCommsShadowSettingsPage() {
  const { imei = "" } = useParams<{ imei: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdminScope = location.pathname.startsWith("/admin/");
  const scopedTenantId = searchParams.get("tenantId")?.trim() || undefined;
  const returnPath = readSafeReturnPath(searchParams.get("returnTo"), isAdminScope ? "/admin/devices" : "/devices");
  const adminRedirectPath = getAdminShadowSettingsRedirectPath(location.pathname, imei, scopedTenantId, getRole());
  const tenantScopeOptions = { requireTenant: isAdminScope, useActingTenantFallback: !isAdminScope };
  const deviceQuery = useDeviceForTenant(imei, scopedTenantId, tenantScopeOptions);
  const configQuery = useDeviceConfigurationForTenant(imei, scopedTenantId, tenantScopeOptions);
  const patchConfig = usePatchDeviceConfiguration();

  const [contacts, setContacts] = useState<ShadowContacts>(emptyContacts);
  const [allowlistNumbers, setAllowlistNumbers] = useState<string[]>([""]);
  const [responseNumbers, setResponseNumbers] = useState<string[]>([""]);
  const [listenInNumber, setListenInNumber] = useState("");
  // SMS text per button, keyed by its schema path. Not part of configValues:
  // the generic form state cannot tell an emptied message from one never set,
  // and would remove the key instead of writing "".
  const [buttonMessages, setButtonMessages] = useState<Record<string, string>>({});
  const [checkInMinutes, setCheckInMinutes] = useState<string>(String(checkInDefaultMinutes));
  const [configValues, setConfigValues] = useState<ConfigValues>({});
  // Paths the operator has reset to their schema default in this session. They
  // are SHOWN with the default value but saved as ABSENT, so the device follows
  // the default instead of being pinned to a copy of today's value. Editing a
  // field again takes it back out of this set — it becomes an override once more.
  const [resetPaths, setResetPaths] = useState<Set<string>>(() => new Set());
  // SOS and button-setting paths the operator changed (or reset) since the page
  // loaded. Together with what is already stored for the device, this decides
  // which of those keys a save sends - see isValueOnlyPath.
  const [touchedPaths, setTouchedPaths] = useState<Set<string>>(() => new Set());
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const deviceTypesQuery = useDeviceTypes();
  const deviceModelsQuery = useDeviceModels();
  const deviceType = useMemo(
    () => (deviceTypesQuery.data?.data ?? []).find((t) => t.deviceTypeId === configQuery.data?.data?.deviceTypeId),
    [deviceTypesQuery.data?.data, configQuery.data?.data?.deviceTypeId],
  );
  // Secret fields (kind secret): whether each is set, reported and pending,
  // and whether this caller may see the value - only tenant_admin and above,
  // and only when the API sent it.
  const secretStates = configQuery.data?.data?.secrets;
  const canRevealSecrets = hasMinRole("tenant_admin") && !configQuery.data?.data?.secretsRedacted;
  // Schema-driven fields not already covered by the dedicated contact and
  // allowlist controls below (audio, BLE, crash & power, telemetry, …).
  const extraFields = useMemo(
    () =>
      (deviceType?.fields ?? []).filter(
        // Deprecated fields stay in the schema for backward compatibility with
        // stored payloads, but must not be offered as something to set.
        (f) =>
          !f.deprecated &&
          !f.path.startsWith("buttons.") &&
          f.path !== allowlistPath &&
          // Inbound keys render above the allowlist instead — see inboundFields.
          !f.path.startsWith("inbound.") &&
          // SOS keys render under the contact numbers instead — see sosFields.
          !f.path.startsWith("sos."),
      ),
    [deviceType],
  );

  // The SOS settings belong WITH the SOS number, not in the generic "Device
  // configuration" block at the bottom of the page: whether an SOS places a
  // call at all, and who it rings, is read together with the number it dials.
  // The response numbers are left out of this set because they have their own
  // row editor — see responseNumbersPath.
  const sosFields = useMemo(
    () =>
      (deviceType?.fields ?? []).filter(
        (f) => !f.deprecated && f.path.startsWith("sos.") && f.path !== responseNumbersPath,
      ),
    [deviceType],
  );
  const responseNumbersField = useMemo(
    () => (deviceType?.fields ?? []).find((f) => f.path === responseNumbersPath && !f.deprecated),
    [deviceType],
  );
  const maxResponseNumbers =
    responseNumbersField?.maxItems && responseNumbersField.maxItems > 0
      ? responseNumbersField.maxItems
      : defaultMaxResponseNumbers;

  // The button settings belong WITH the button numbers, for the same reason.
  // The action and message of each button are drawn in that button's own block
  // below; whatever else the schema puts under "buttons" (the hold time, the
  // retry settings) applies to all three and is drawn once.
  const buttonSettingFields = useMemo(
    () => (deviceType?.fields ?? []).filter((f) => !f.deprecated && isButtonSettingPath(f.path)),
    [deviceType],
  );
  const buttonFieldByPath = useMemo(
    () => new Map(buttonSettingFields.map((f) => [f.path, f])),
    [buttonSettingFields],
  );
  const sharedButtonFields = useMemo(
    () =>
      buttonSettingFields.filter(
        (f) => !programmableButtons.some((button) => f.path === button.actionPath || f.path === button.messagePath),
      ),
    [buttonSettingFields],
  );
  // Button settings held in the generic form state: everything but the SMS
  // messages, which have their own (see buttonMessages).
  const buttonValueFields = useMemo(
    () => buttonSettingFields.filter((f) => !programmableButtons.some((button) => f.path === button.messagePath)),
    [buttonSettingFields],
  );
  // Fields that open on what the device reports when nothing is stored for it.
  const valueOnlyFields = useMemo(() => [...sosFields, ...buttonValueFields], [sosFields, buttonValueFields]);

  // Inbound policy and ring count belong WITH the allowlist, not in the generic
  // "Device configuration" block far below it: the policy decides what happens
  // to a caller, and the allowlist is the exception list to that policy. Split
  // out here so they render immediately above it, in schema order.
  const inboundFields = useMemo(
    () =>
      (deviceType?.fields ?? []).filter(
        (f) =>
          !f.deprecated &&
          f.path.startsWith("inbound.") &&
          f.path !== allowlistPath &&
          // Drawn as its own single-number control — see listenInNumbersPath.
          f.path !== listenInNumbersPath,
      ),
    [deviceType],
  );
  const listenInField = useMemo(
    () => (deviceType?.fields ?? []).find((f) => f.path === listenInNumbersPath && !f.deprecated),
    [deviceType],
  );

  // Every schema field the form loads and saves. The sets above only decide
  // WHERE a field renders; loading and saving must cover them all, or a field
  // drawn in the inbound, SOS or buttons block opens without its stored value
  // and is dropped on save.
  const formFields = useMemo(
    () => [...extraFields, ...inboundFields, ...valueOnlyFields],
    [extraFields, inboundFields, valueOnlyFields],
  );
  // Secret fields are never loaded into the editable form: their box holds only
  // what the operator types. A preloaded value would be resent on every save
  // and overwrite a password the device has adopted since the page loaded.
  const secretFields = useMemo(() => formFields.filter((f) => f.kind === "secret"), [formFields]);
  // The stored value, shown read-only on Reveal to a caller who may read it.
  const storedSecretValues = useMemo(
    () =>
      canRevealSecrets && secretFields.length > 0
        ? initialConfigValues(secretFields, [configQuery.data?.data?.overrides])
        : undefined,
    [canRevealSecrets, secretFields, configQuery.data?.data?.overrides],
  );

  useEffect(() => {
    if (!adminRedirectPath) return;

    stopActingAsCompany();
    navigate(adminRedirectPath, { replace: true });
  }, [adminRedirectPath, navigate]);

  useEffect(() => {
    if (dirty || !configQuery.data?.data) return;
    const config = configQuery.data.data;
    setContacts(buildContacts(config));
    setAllowlistNumbers(readAllowlist(config));
    setResponseNumbers(readResponseNumbers(config));
    setListenInNumber(readListenInNumber(config));
    setCheckInMinutes(readCheckInMinutes(config));
    setButtonMessages(readButtonMessages(config));
    setTouchedPaths(new Set());
    if (formFields.length > 0) {
      setConfigValues({
        ...initialConfigValues(formFields, [config.overrides, config.payload, deviceType?.defaultPayload]),
        // Where nothing is stored for this device, what it reports comes
        // before the schema default: that is what it is actually running.
        ...initialConfigValues(valueOnlyFields, [
          config.overrides,
          config.reported?.payload,
          config.payload,
          deviceType?.defaultPayload,
        ]),
        // Blank until typed - see secretFields.
        ...Object.fromEntries(secretFields.map((f) => [f.path, ""])),
      });
    }
  }, [configQuery.data?.data, formFields, valueOnlyFields, secretFields, deviceType, dirty]);

  // What the device reports for each SOS and button setting, shown as "On
  // device: X" where the staged value differs. Only paths it actually reported
  // are included.
  const deviceValues = useMemo(() => {
    const reported = configQuery.data?.data?.reported?.payload;
    if (!reported) return undefined;
    const reportedFields = valueOnlyFields.filter((field) => hasValueAtPath(reported, field.path));
    return initialConfigValues(reportedFields, [reported]);
  }, [configQuery.data?.data?.reported?.payload, valueOnlyFields]);
  const reportedResponseNumbers = useMemo(
    () => readReportedResponseNumbers(configQuery.data?.data),
    [configQuery.data?.data],
  );
  const responseNumbersDifferOnDevice =
    reportedResponseNumbers !== null &&
    JSON.stringify(reportedResponseNumbers) !== JSON.stringify(normaliseAllowlist(responseNumbers));
  const reportedListenInNumbers = useMemo(
    () => readReportedListenInNumbers(configQuery.data?.data),
    [configQuery.data?.data],
  );
  const listenInDiffersOnDevice =
    reportedListenInNumbers !== null &&
    JSON.stringify(reportedListenInNumbers) !== JSON.stringify(normaliseAllowlist([listenInNumber]));

  function touchPaths(paths: string[]) {
    setTouchedPaths((current) => {
      if (paths.every((path) => current.has(path))) return current;
      const next = new Set(current);
      for (const path of paths) next.add(path);
      return next;
    });
  }

  function updateCheckInMinutes(value: string) {
    setDirty(true);
    setSaved(false);
    setCheckInMinutes(value);
  }

  function updateContact(key: ShadowContactKey, value: string) {
    setDirty(true);
    setSaved(false);
    setContacts((current) => ({ ...current, [key]: value }));
  }

  function updateConfigValue(path: string, value: string) {
    setDirty(true);
    setSaved(false);
    setConfigValues((current) => ({ ...current, [path]: value }));
    if (isValueOnlyPath(path)) touchPaths([path]);
    // Touching a reset field makes it an explicit override again.
    setResetPaths((current) => {
      if (!current.has(path)) return current;
      const next = new Set(current);
      next.delete(path);
      return next;
    });
  }

  // Reset one section's fields to the schema defaults. Fills the controls so the
  // operator sees exactly what will apply, and marks those paths for removal
  // from the stored override on save; nothing reaches the device until Save.
  //
  // SOS and button settings are the exception: they are never removed. The
  // defaults are written as values, so the device is told to go back to them.
  function resetSectionToDefaults(_section: string, sectionFields: DeviceTypeFieldDefinition[]) {
    setDirty(true);
    setSaved(false);
    const defaults = initialConfigValues(sectionFields, [deviceType?.defaultPayload]);
    const valuePaths: string[] = [];
    for (const field of sectionFields) {
      if (!isValueOnlyPath(field.path)) continue;
      // A schema without a seeded default falls back to the firmware's own; a
      // field with neither keeps its current value rather than going blank,
      // because a blank would be saved as a removed key.
      if (defaults[field.path] === "") {
        if (field.deviceDefault) {
          defaults[field.path] = field.deviceDefault;
        } else {
          delete defaults[field.path];
          continue;
        }
      }
      valuePaths.push(field.path);
    }
    setConfigValues((current) => ({ ...current, ...defaults }));
    touchPaths(valuePaths);
    setResetPaths((current) => {
      const next = new Set(current);
      for (const field of sectionFields) {
        if (!isValueOnlyPath(field.path)) next.add(field.path);
      }
      return next;
    });
  }

  function updateAllowlistNumber(index: number, value: string) {
    setDirty(true);
    setSaved(false);
    setAllowlistNumbers((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  }

  function addAllowlistNumber() {
    setDirty(true);
    setSaved(false);
    setAllowlistNumbers((current) => (current.length >= maxAllowlistNumbers ? current : [...current, ""]));
  }

  function removeAllowlistNumber(index: number) {
    setDirty(true);
    setSaved(false);
    setAllowlistNumbers((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [""];
    });
  }

  function updateResponseNumber(index: number, value: string) {
    setDirty(true);
    setSaved(false);
    touchPaths([responseNumbersPath]);
    setResponseNumbers((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  }

  function addResponseNumber() {
    setDirty(true);
    setSaved(false);
    touchPaths([responseNumbersPath]);
    setResponseNumbers((current) => (current.length >= maxResponseNumbers ? current : [...current, ""]));
  }

  function removeResponseNumber(index: number) {
    setDirty(true);
    setSaved(false);
    touchPaths([responseNumbersPath]);
    setResponseNumbers((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [""];
    });
  }

  function updateListenInNumber(value: string) {
    setDirty(true);
    setSaved(false);
    touchPaths([listenInNumbersPath]);
    setListenInNumber(value);
  }

  function updateButtonMessage(path: string, value: string) {
    setDirty(true);
    setSaved(false);
    touchPaths([path]);
    setButtonMessages((current) => ({ ...current, [path]: singleLine(value) }));
  }

  function maxMessageLength(button: ProgrammableButton): number {
    const max = buttonFieldByPath.get(button.messagePath)?.max;
    return max && max > 0 ? max : defaultMaxMessageLength;
  }

  // A save sends an SOS or button setting only when it is already stored for
  // this device or was changed here; anything else is left as the device has it.
  function sendsPath(path: string): boolean {
    return touchedPaths.has(path) || hasValueAtPath(configQuery.data?.data?.overrides, path);
  }

  // The SMS texts a save writes, by path, trimmed. See sendsPath. A message is
  // written only while its button is set to Send SMS, which is the only time
  // its box is on the page: text typed and then hidden by a change of action
  // is neither checked nor sent, and a stored one stays as it is stored.
  function messagesToSend(): Record<string, string> {
    const messages: Record<string, string> = {};
    for (const button of programmableButtons) {
      if (!buttonFieldByPath.has(button.messagePath) || !sendsPath(button.messagePath)) continue;
      if ((configValues[button.actionPath] ?? "") !== smsAction) continue;
      messages[button.messagePath] = (buttonMessages[button.messagePath] ?? "").trim();
    }
    return messages;
  }

  function validateButtons(messages: Record<string, string>): string {
    for (const button of programmableButtons) {
      const message = messages[button.messagePath];
      if (message !== undefined) {
        const messageError = validateButtonMessage(button.label, message, maxMessageLength(button));
        if (messageError) return messageError;
      }
      // A number is asked for only where the action was chosen on this page.
      // A button left on an action it already had may still have its number
      // cleared, as it always could.
      const action = configValues[button.actionPath] ?? "";
      if (!buttonFieldByPath.has(button.actionPath) || !touchedPaths.has(button.actionPath)) continue;
      const choice = buttonFieldByPath.get(button.actionPath)?.choices?.find((c) => c.value === action);
      if (actionsNeedingNumber.includes(action) && !sanitisePhoneNumber(contacts[button.key])) {
        return `${button.label} is set to ${choice?.label ?? action} and needs a number.`;
      }
      // The same for the text: a device set to Send SMS with nothing to send
      // shows its error light and sends nothing. The box opens on the text
      // stored or reported for the button, so that counts.
      if (
        action === smsAction &&
        buttonFieldByPath.has(button.messagePath) &&
        !(buttonMessages[button.messagePath] ?? "").trim()
      ) {
        return `${button.label} is set to ${choice?.label ?? action} and needs a message.`;
      }
    }
    return "";
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSaved(false);

    const messages = messagesToSend();
    const validationError =
      validateContacts(contacts) ||
      validateButtons(messages) ||
      (responseNumbersField ? validateResponseNumbers(responseNumbers, maxResponseNumbers) : "") ||
      (listenInField ? validateListenInNumber(listenInNumber) : "") ||
      validateAllowlist(allowlistNumbers) ||
      validateSecrets(formFields, configValues);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (isAdminScope && !scopedTenantId) {
      setError("This admin shadow settings page needs a tenant context for the selected device.");
      return;
    }

    try {
      const baseOverrides = buildOverrides(
        configQuery.data?.data,
        contacts,
        allowlistNumbers,
        checkInMinutes,
        responseNumbersField && sendsPath(responseNumbersPath) ? responseNumbers : null,
        messages,
        listenInField && sendsPath(listenInNumbersPath) ? listenInNumber : null,
      );
      const savedFields = formFields.filter((field) => !isValueOnlyPath(field.path) || sendsPath(field.path));
      // Secret fields must stay in savedFields: the base is cloned from the
      // stored override, which carries the stored secret for a tenant_admin,
      // and only buildConfigOverrides removes it again when the box is blank.
      // Left out, a secret is kept by the server; sent, only as typed.
      const built = buildConfigOverrides(savedFields, configValues, baseOverrides);
      // Fields reset to their default are removed rather than written. An
      // explicit override equal to today's default pins the device to it, so a
      // later change to the schema default would never reach that device.
      const merged = clearPaths(built, resetPaths);
      // Drop any key the current device-type schema no longer defines. The base
      // is cloned from the stored override, which may still carry fields renamed
      // or removed from the schema (e.g. a pre-migration `audio.system_volume`);
      // the backend rejects those as unsupported, so saving would fail forever.
      // Only prune when we actually have the schema — never blind-wipe config we
      // can't render because the device-type lookup failed to load.
      // Device settings are not schema fields, so they must be named explicitly
      // or the prune below would strip the cadence on every save.
      const schemaPaths = (deviceType?.fields ?? []).map((field) => field.path);
      const allowedPaths = schemaPaths.length > 0 ? [...schemaPaths, checkInPath] : [];
      const overrides = allowedPaths.length > 0 ? pruneToAllowedPaths(merged, allowedPaths) : merged;
      await patchConfig.mutateAsync({
        imei,
        tenantId: scopedTenantId,
        overrides,
      });
      setDirty(false);
      setSaved(true);
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to save shadow settings"));
    }
  }

  const device = deviceQuery.data?.data;
  const isLoading = deviceQuery.isLoading || configQuery.isLoading;

  return (
    <div>
      <button
        onClick={() => navigate(returnPath)}
        className="mb-4 flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        <ArrowLeft size={14} /> {isAdminScope ? "Back to all devices" : "Back to devices"}
      </button>

      {isAdminScope && !scopedTenantId && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          This admin shadow settings page needs a tenant context for the selected device. Return to All Devices and open Shadow from the device row.
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">Shadow settings</p>
            <h1 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">Contact numbers</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">
              IMEI: <span className="font-mono text-gray-700 dark:text-riot-navy-100">{imei}</span>
              {device?.model ? <span> · {deviceModelLabel(device.model, deviceModelsQuery.data?.data ?? [])}</span> : null}
            </p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-riot-gold-500/15 text-riot-gold-500">
            <Settings2 size={20} />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading shadow settings...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* With button settings in the schema, each button's number is
                  drawn in that button's own block below, beside its action. */}
              {(buttonSettingFields.length > 0 ? contactFields.filter((field) => field.key === "sos") : contactFields).map((field) => (
                <label key={field.key} className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-gray-700 dark:text-riot-navy-200">{field.label}</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={contacts[field.key]}
                    onChange={(event) => updateContact(field.key, event.target.value)}
                    placeholder={field.placeholder}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
                  />
                </label>
              ))}
            </div>

            {buttonSettingFields.length > 0 && (
              <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Button actions</h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    What the left, middle and right buttons do when they are held. Only the
                    actions, messages and settings you change here, or that are already saved for
                    this device, are sent to it; the rest stay as the device has them.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {programmableButtons.map((button) => {
                    const actionField = buttonFieldByPath.get(button.actionPath);
                    const messageField = buttonFieldByPath.get(button.messagePath);
                    const action = configValues[button.actionPath] ?? "";
                    const choices = actionField?.choices ?? [];
                    const selectedChoice = choices.find((choice) => choice.value === action);
                    const usesNumber = !actionField || actionsNeedingNumber.includes(action);
                    const message = buttonMessages[button.messagePath] ?? "";
                    const maxLength = maxMessageLength(button);
                    const messageLength = smsLength(message);
                    const reportedAction = deviceValues?.[button.actionPath];
                    const reportedMessage = readUnknownAtPath(
                      configQuery.data?.data?.reported?.payload,
                      button.messagePath,
                    );
                    const idBase = `shadow-button-${button.key}`;
                    return (
                      <section
                        key={button.key}
                        aria-label={button.label}
                        className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-riot-navy-700 dark:bg-riot-navy-900/60"
                      >
                        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-riot-navy-300">
                          {button.label}
                        </h3>
                        {actionField && (
                          <div>
                            <label htmlFor={`${idBase}-action`} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                              {button.label} action
                            </label>
                            <select
                              id={`${idBase}-action`}
                              value={action}
                              onChange={(event) => updateConfigValue(button.actionPath, event.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                            >
                              {action === "" && (
                                <option value="" disabled>
                                  Select…
                                </option>
                              )}
                              {/* A stored action outside the listed set is shown
                                  as-is so it round-trips unchanged. */}
                              {action !== "" && !selectedChoice && <option value={action}>{`Custom (${action})`}</option>}
                              {choices.map((choice) => (
                                <option key={choice.value} value={choice.value}>
                                  {choice.label}
                                </option>
                              ))}
                            </select>
                            {selectedChoice?.description && (
                              <p className="mt-1 text-[11px] text-gray-600 dark:text-riot-navy-300">{selectedChoice.description}</p>
                            )}
                            {reportedAction !== undefined && reportedAction !== action && (
                              <p
                                className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                                data-testid={`${idBase}-action-on-device`}
                              >
                                On device: {choices.find((choice) => choice.value === reportedAction)?.label ?? reportedAction}
                              </p>
                            )}
                          </div>
                        )}
                        <div>
                          <label htmlFor={`${idBase}-number`} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                            {button.label} number
                            {actionField && usesNumber && (
                              <span className="ml-1 text-riot-gold-600 dark:text-riot-gold-400" title="Required for this action">
                                *
                              </span>
                            )}
                          </label>
                          <input
                            id={`${idBase}-number`}
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            aria-required={actionField ? usesNumber : undefined}
                            value={contacts[button.key]}
                            onChange={(event) => updateContact(button.key, event.target.value)}
                            placeholder={button.placeholder}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
                          />
                          {actionField && (
                            <p className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">
                              {usesNumber
                                ? `The number this button ${action === smsAction ? "sends its SMS to" : "calls"}.`
                                : `Not used by ${selectedChoice?.label ?? "this action"}. It is kept for when the action is changed back.`}
                            </p>
                          )}
                        </div>
                        {messageField && action === smsAction && (
                          <div>
                            <label htmlFor={`${idBase}-message`} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                              {button.label} SMS message
                            </label>
                            <textarea
                              id={`${idBase}-message`}
                              rows={3}
                              maxLength={maxLength}
                              value={message}
                              onChange={(event) => updateButtonMessage(button.messagePath, event.target.value)}
                              aria-describedby={`${idBase}-message-count`}
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                            />
                            <div className="mt-1 flex items-start justify-between gap-3 text-[11px] text-gray-500 dark:text-riot-navy-400">
                              <p>
                                Plain letters, digits and punctuation.
                                {messageLength !== message.length && " Each of ^ { } [ ] ~ | \\ counts as two."} Clear it
                                and save to clear it on the device.
                              </p>
                              <p
                                id={`${idBase}-message-count`}
                                className={
                                  messageLength > maxLength
                                    ? "shrink-0 font-mono tabular-nums text-red-600 dark:text-red-400"
                                    : "shrink-0 font-mono tabular-nums"
                                }
                              >
                                {messageLength} / {maxLength}
                              </p>
                            </div>
                            {typeof reportedMessage === "string" && reportedMessage !== message.trim() && (
                              <p
                                className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                                data-testid={`${idBase}-message-on-device`}
                              >
                                On device: {reportedMessage === "" ? "(empty)" : reportedMessage}
                              </p>
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
                {sharedButtonFields.length > 0 && (
                  <ConfigFieldsForm
                    fields={sharedButtonFields}
                    values={configValues}
                    onChange={updateConfigValue}
                    onResetSection={resetSectionToDefaults}
                    secretStates={secretStates}
                    canRevealSecrets={canRevealSecrets}
                    storedSecretValues={storedSecretValues}
                    deviceValues={deviceValues}
                    idPrefix="shadow-buttons"
                  />
                )}
              </div>
            )}

            {(sosFields.length > 0 || responseNumbersField) && (
              <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">SOS behaviour</h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    What this device does when its SOS button is held. The SOS contact number is
                    set above. Only the SOS settings you change here, or that are already saved
                    for this device, are sent to it; the rest stay as the device has them.
                  </p>
                </div>
                {sosFields.length > 0 && (
                  <ConfigFieldsForm
                    fields={sosFields}
                    values={configValues}
                    onChange={updateConfigValue}
                    onResetSection={resetSectionToDefaults}
                    secretStates={secretStates}
                    canRevealSecrets={canRevealSecrets}
                    storedSecretValues={storedSecretValues}
                    deviceValues={deviceValues}
                    idPrefix="shadow-sos"
                  />
                )}
                {responseNumbersField && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{responseNumbersField.label}</h3>
                        <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                          {responseNumbersField.description ||
                            `Up to ${maxResponseNumbers} numbers of the people who respond to an SOS.`}{" "}
                          Remove every number and save to clear the list on the device.
                        </p>
                        {responseNumbersDifferOnDevice && (
                          <p
                            className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                            data-testid="shadow-sos-response-numbers-on-device"
                          >
                            On device: {reportedResponseNumbers!.length > 0 ? reportedResponseNumbers!.join(", ") : "none"}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={addResponseNumber}
                        disabled={responseNumbers.length >= maxResponseNumbers}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                      >
                        <Plus size={14} /> Add response number
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {responseNumbers.map((number, index) => (
                        <div key={index} className="flex gap-2">
                          <label className="flex flex-1 flex-col gap-1.5">
                            <span className="text-sm font-medium text-gray-700 dark:text-riot-navy-200">Response number {index + 1}</span>
                            <input
                              type="tel"
                              inputMode="tel"
                              autoComplete="tel"
                              value={number}
                              onChange={(event) => updateResponseNumber(index, event.target.value)}
                              placeholder={responseNumbersField.placeholder || "+27820000000"}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeResponseNumber(index)}
                            className="mt-7 inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-red-600 dark:border-riot-navy-600 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700 dark:hover:text-red-300"
                            aria-label={`Remove response number ${index + 1}`}
                            title="Remove number"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(inboundFields.length > 0 || listenInField) && (
              <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Inbound calls</h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    How this device handles an incoming call. The allowlist below overrides this
                    for the numbers on it.
                  </p>
                </div>
                {inboundFields.length > 0 && (
                  <ConfigFieldsForm
                    fields={inboundFields}
                    values={configValues}
                    onChange={updateConfigValue}
                    onResetSection={resetSectionToDefaults}
                    secretStates={secretStates}
                    canRevealSecrets={canRevealSecrets}
                    storedSecretValues={storedSecretValues}
                    idPrefix="shadow-inbound"
                  />
                )}
                {listenInField && (
                  <div className="max-w-md space-y-1.5">
                    <label htmlFor="shadow-listen-in-number" className="block text-sm font-medium text-gray-700 dark:text-riot-navy-200">
                      {listenInField.label}
                    </label>
                    <input
                      id="shadow-listen-in-number"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={listenInNumber}
                      onChange={(event) => updateListenInNumber(event.target.value)}
                      placeholder={listenInField.placeholder || "+27820000000"}
                      aria-describedby="shadow-listen-in-warning"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
                    />
                    <p
                      id="shadow-listen-in-warning"
                      className="text-xs text-amber-700 dark:text-amber-400"
                      data-testid="shadow-listen-in-warning"
                    >
                      A call from this number is covert and silent. The device answers it at once,
                      under every inbound policy and during an SOS, with the speaker turned off and
                      the microphone on, and nothing on the device shows that a call is up. Anyone
                      calling from it can hear the vehicle without the people in it knowing. If the
                      device is already on its own SOS call, that call is not dropped: the listen-in
                      caller gets busy or waiting until it ends. Set it only where listening in has
                      been approved.
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-riot-navy-400">
                      One number. It may also be an SOS response number, an allowlist number or a
                      button number; a call from it is still a silent listen-in. Clear it and save to
                      remove it from the device.
                    </p>
                    {listenInDiffersOnDevice && (
                      <p
                        className="text-[11px] font-medium text-amber-700 dark:text-amber-400"
                        data-testid="shadow-listen-in-on-device"
                      >
                        On device: {reportedListenInNumbers!.length > 0 ? reportedListenInNumbers!.join(", ") : "none"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Inbound allowlist</h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">Numbers that can call this device and be auto-answered.</p>
                </div>
                <button
                  type="button"
                  onClick={addAllowlistNumber}
                  disabled={allowlistNumbers.length >= maxAllowlistNumbers}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                >
                  <Plus size={14} /> Add number
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {allowlistNumbers.map((number, index) => (
                  <div key={index} className="flex gap-2">
                    <label className="flex flex-1 flex-col gap-1.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-riot-navy-200">Allowed number {index + 1}</span>
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={number}
                        onChange={(event) => updateAllowlistNumber(index, event.target.value)}
                        placeholder="+27673382771"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeAllowlistNumber(index)}
                      className="mt-7 inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-red-600 dark:border-riot-navy-600 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700 dark:hover:text-red-300"
                      aria-label={`Remove allowed number ${index + 1}`}
                      title="Remove number"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Check-in cadence</h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                  How often this device wakes, connects, and reconciles its configuration and
                  firmware with the cloud. Leave blank to keep whatever the device is already
                  using.
                </p>
              </div>
              <div className="max-w-xs">
                <label
                  htmlFor="shadow-checkin-minutes"
                  className="block text-xs font-medium text-gray-700 dark:text-riot-navy-200"
                >
                  Minutes between check-ins
                </label>
                <input
                  id="shadow-checkin-minutes"
                  type="number"
                  inputMode="numeric"
                  min={checkInMinMinutes}
                  max={checkInMaxMinutes}
                  step={1}
                  value={checkInMinutes}
                  onChange={(e) => updateCheckInMinutes(e.target.value)}
                  placeholder={`${checkInDefaultMinutes} (default)`}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-400">
                  1–1440 minutes. The device applies this from the shadow and keeps it across
                  reboots.
                </p>
                {isAggressiveCadence(checkInMinutes) && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    A {clampCheckInMinutes(checkInMinutes)}-minute cadence is a bench setting. On a
                    fitted device it multiplies cellular data and AWS message cost by roughly{" "}
                    {Math.round(checkInDefaultMinutes / (clampCheckInMinutes(checkInMinutes) || 1))}×
                    against the hourly default, and keeps the modem awake far more of the time.
                  </p>
                )}
              </div>
            </div>

            {extraFields.length > 0 && (
              <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Device configuration</h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    Audio, BLE, crash &amp; power and telemetry settings delivered to the device shadow.
                  </p>
                </div>
                <ConfigFieldsForm
                  fields={extraFields}
                  values={configValues}
                  onChange={updateConfigValue}
                  onResetSection={resetSectionToDefaults}
                  secretStates={secretStates}
                  canRevealSecrets={canRevealSecrets}
                  storedSecretValues={storedSecretValues}
                  idPrefix="shadow-config"
                />
              </div>
            )}

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            {saved && <p className="text-sm text-green-700 dark:text-green-300">Shadow settings saved.</p>}

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={patchConfig.isPending}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {patchConfig.isPending ? "Saving..." : "Save shadow settings"}
              </button>
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
